import type { AxiosInstance } from 'axios';
import type {
    Block,
    BlockWritePayload,
    FieldCreatePayload,
    FieldReorderPayload,
    FieldUpdatePayload,
    GetBlocksRes,
    UsedOptionsRes,
} from '../interfaces/block';

/**
 * Nav: **Advanced config** — the block library. Owned by whoever builds that page; see
 * docs/CRM_SECTION_CONTRACT.md §5 for the endpoint inventory and §6 for ownership.
 *
 * Reached as `getCrmRequests().blocks.<method>()`. Constructed once by `AqCrmRequests`, which owns
 * the injected axios instance — this class must never create its own (CRM_ARCHITECTURE.md §7).
 * Every method needs an explicit return type (FRONTEND_PATTERNS.md).
 */
export class BlocksApi {
    protected readonly client: AxiosInstance;

    protected readonly baseURL: string;

    constructor(client: AxiosInstance, baseURL: string) {
        this.client = client;
        this.baseURL = baseURL;
    }

    private get blocksUrl(): string {
        return `${this.baseURL}/blocks`;
    }

    /**
     * The whole catalogue, unpaginated — Advanced config's rail and Configuration CRM's `BLOCS
     * DISPONIBLES` palette both render all of it, and it is small by design.
     *
     * Landed with the Configuration CRM seam rather than by Advanced config's Track A: two pages read it,
     * and neither should wait on the other for the one call they share (§6).
     */
    async list(): Promise<Block[]> {
        const response = await this.client.get<GetBlocksRes>(this.blocksUrl, {
            params: { pagination: false },
        });
        return response.data.blocks;
    }

    async usedOptions(blockId: string): Promise<UsedOptionsRes> {
        const response = await this.client.get<UsedOptionsRes>(`${this.blocksUrl}/${blockId}/used-options`);
        return response.data;
    }

    async create(): Promise<Block> {
        const response = await this.client.post<Block>(this.blocksUrl);
        return response.data;
    }

    async update(blockId: string, payload: BlockWritePayload): Promise<Block> {
        const response = await this.client.patch<Block>(`${this.blocksUrl}/${blockId}`, payload);
        return response.data;
    }

    async remove(blockId: string): Promise<Block> {
        const response = await this.client.delete<Block>(`${this.blocksUrl}/${blockId}`);
        return response.data;
    }

    async createField(blockId: string, payload: FieldCreatePayload): Promise<Block> {
        const response = await this.client.post<Block>(`${this.blocksUrl}/${blockId}/fields`, payload);
        return response.data;
    }

    async updateField(
        blockId: string,
        fieldId: string,
        payload: FieldUpdatePayload,
    ): Promise<Block> {
        const response = await this.client.patch<Block>(
            `${this.blocksUrl}/${blockId}/fields/${fieldId}`,
            payload,
        );
        return response.data;
    }

    async deleteField(blockId: string, fieldId: string): Promise<Block> {
        const response = await this.client.delete<Block>(`${this.blocksUrl}/${blockId}/fields/${fieldId}`);
        return response.data;
    }

    async reorderFields(blockId: string, payload: FieldReorderPayload): Promise<Block> {
        const response = await this.client.patch<Block>(`${this.blocksUrl}/${blockId}/fields/reorder`, payload);
        return response.data;
    }
}
